import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { isWallet } from '@model/WalletDto'

import { Loading } from '@shared/components/Loading/Loading'
import { SuccessModal as SharedSuccessModal } from '@shared/modals/Success.modal'

import {
  PaymentAgentTransferForm,
  PaymentAgentTransferFormValues,
} from './PaymentAgentTransferForm/PaymentAgentTransferForm'

const usePaymentAgentWalletTransferMutate = () => {
  const { walletId } = useParams<{ walletId?: string }>()
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { addSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const handleError = (error: AxiosError<{ message?: string; localizedMessage?: string }>) => {
    const message =
      error?.response?.data?.localizedMessage ||
      error?.response?.data?.message ||
      t('errors.Something went wrong')

    addSnackbar.error({ message })
  }

  return useMutation({
    mutationFn: async (values: PaymentAgentTransferFormValues) => {
      if (!values?.campaignName) {
        return await apiClient.transactionDepositFromPaymentAgent(locale, {
          clientWalletId: values.clientWallet.id,
          paymentAgentWalletId: walletId || '',
          amount: values?.amount || 0,
          system: values.system,
          account: values.account,
          comment: values.comment,
          fileName: values.fileName,
          base64Content: values.base64Content,
        })
      }

      if (values?.campaignName) {
        const tradingAccountCampaign = await apiClient.tradingAccountValidateCampaignAccount(
          values.clientWallet.id,
          values.campaignName
        )
        const tradingAccountId = tradingAccountCampaign.tradingAccountId
        const [tradingAccountCampaignId] = tradingAccountCampaign.campaigns

        return await apiClient.transactionDepositFromPaymentAgent(locale, {
          clientWalletId: values.clientWallet.id,
          paymentAgentWalletId: walletId || '',
          amount: values?.amount || 0,
          system: values.system,
          account: values.account,
          comment: values.comment,
          fileName: values.fileName,
          base64Content: values.base64Content,
          details: [
            { name: 'TradingAccountId', value: tradingAccountId },
            { name: 'CampaignId', value: tradingAccountCampaignId },
          ],
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transaction'] })
    },
    onError: handleError,
    retry: false,
  })
}

export const PaymentAgentWalletTransferPage: React.FC = () => {
  const navigate = useNavigate()

  const [values, setValues] = useState<PaymentAgentTransferFormValues | undefined>()
  const mutation = usePaymentAgentWalletTransferMutate()
  const { isDefaultCFDProductType } = useProductReadContext()

  const handleSubmit = async (values: PaymentAgentTransferFormValues) => {
    setValues(values)
    await mutation.mutateAsync(values)
  }

  const handleSuccess = async () => {
    navigate('/dashboard/payment-agent/transaction-history')
  }

  const successModal = useModal(SharedSuccessModal)

  const handlePageExitConfirmation = () => {
    navigate(
      isDefaultCFDProductType() ? CommonPaths.PaymentAgentWallets : CommonPaths.TradersRoomBalances
    )
  }
  const { t } = useTranslation()

  useEffect(() => {
    if (mutation.data && values) {
      const items = isWallet(values.clientWallet)
        ? [
            { label: t('From'), value: `${values.clientWallet.currency.id} ${t('Wallet.Wallet')}` },
            { label: t('Amount'), value: `${values.amount} ${values.clientWallet.currency.id}` },
            { label: t('To'), value: values.clientWallet.name },
          ]
        : []

      successModal.open({
        title: t('Funds transferred'),
        items,
        onPrimaryClick: handleSuccess,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.data])

  return (
    <React.Fragment>
      <Loading isLoading={mutation.isPending} showLoadingIcon text={t('Wallet.Transferring ...')}>
        <PaymentAgentTransferForm
          data={values}
          onSubmit={handleSubmit}
          onCancel={handlePageExitConfirmation}
        />
      </Loading>
    </React.Fragment>
  )
}
